import { ArrowRight } from "@gravity-ui/icons";
import {
  Button,
  Flex,
  Icon,
  SegmentedRadioGroup,
  Switch,
  Text,
} from "@gravity-ui/uikit";
import {
  RoundingDirection,
  RoundingOption,
  TransformersEnum,
} from "definitions/exporter";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslate } from "src/i18n";

import { ExportTaskFormValues } from "../../ExportTasksForm.types";
import {
  DIRECTION_OPTIONS,
  FRESH_ROUNDING_OPTION,
  STEP_OPTIONS,
} from "./RoundingOptionForm.constants";
import css from "./RoundingOptionForm.module.scss";
import { roundPrice } from "./roundPrice";

const DEFAULT_SAMPLE = 1587;
// Примеры подобраны так, чтобы показать разное поведение: типичная цена,
// психологическая …90, средняя и мелкая (демонстрирует авто-уменьшение шага).
const PREVIEW_SAMPLES = [1587, 4990, 2350, 850];

const RUB_FORMAT = new Intl.NumberFormat("ru-RU", {
  style: "currency",
  currency: "RUB",
  maximumFractionDigits: 0,
});
const formatRub = (value: number) => RUB_FORMAT.format(value);

const RoundingPreview = ({
  sample,
  option,
}: {
  sample: number;
  option: RoundingOption;
}) => {
  const { t } = useTranslate();

  const rounded = roundPrice(sample, option);
  // Та же формула, что в roundPrice (вкл. нижний кламп Math.max(...,0)) — чтобы
  // подсказка про уменьшение шага совпадала с реальным округлением.
  const requestedZeros = Math.max(Math.trunc(option.zeros), 0);
  const effectiveZeros = Math.min(
    requestedZeros,
    String(Math.floor(sample)).length - 1,
  );
  const clamped = effectiveZeros < requestedZeros;
  const delta = rounded - sample;

  return (
    <div className={css.previewCard}>
      <Flex
        className={css.previewMain}
        alignItems="center"
        justifyContent="center"
        gap={3}
        wrap
        aria-hidden
      >
        <Text className={css.priceFrom}>{formatRub(sample)}</Text>
        <Icon data={ArrowRight} size={20} className={css.previewArrow} />
        <Text className={css.priceTo}>{formatRub(rounded)}</Text>
      </Flex>

      <div className={css.previewNote} aria-live="polite">
        <span className={css.visuallyHidden}>
          {t("export.rounding.preview.sr", "{from} округляется до {to}", {
            from: formatRub(sample),
            to: formatRub(rounded),
          })}
        </span>
        {delta < 0 && (
          <Text variant="body-2" color="warning">
            {t(
              "export.rounding.preview.delta",
              "цена снизилась на {amount} ₽",
              {
                amount: Math.abs(delta),
              },
            )}
          </Text>
        )}
        {clamped && (
          <Text variant="body-2" color="secondary">
            {t(
              "export.rounding.preview.clamp",
              "цена короче шага — округлили до {step} ₽",
              { step: 10 ** effectiveZeros },
            )}
          </Text>
        )}
        {delta === 0 && !clamped && (
          <Text variant="body-2" color="secondary">
            {t("export.rounding.preview.same", "цена уже ровная")}
          </Text>
        )}
      </div>
    </div>
  );
};

export const RoundingOptionForm = () => {
  const { t } = useTranslate();
  const { control, getValues, setValue } =
    useFormContext<ExportTaskFormValues>();
  const [sample, setSample] = useState(DEFAULT_SAMPLE);

  return (
    <Controller
      name="roundingOption"
      control={control}
      render={({ field }) => {
        const value = field.value;

        const handleToggle = (on: boolean) => {
          if (!on) {
            field.onChange(undefined);
            return;
          }
          // roundingOption приоритетнее ROUNDING_PRICE на бэке — снимаем мёртвый
          // трансформер, чтобы не слать в payload перебитую настройку.
          const transformers = getValues("transformers") ?? [];
          if (transformers.includes(TransformersEnum.RoundingPrice)) {
            setValue(
              "transformers",
              transformers.filter((t) => t !== TransformersEnum.RoundingPrice),
            );
          }
          field.onChange(FRESH_ROUNDING_OPTION);
        };

        return (
          <div className={css.root}>
            <Flex direction="column" gap={1}>
              <Switch size="m" checked={!!value} onUpdate={handleToggle}>
                {t("export.rounding.toggle", "Округлять итоговую цену")}
              </Switch>
              <Text variant="body-1" color="secondary">
                {t(
                  "export.rounding.subtitle",
                  "Делаем итоговую цену ровной — например, 1587 ₽ → 1600 ₽. Применяется в самом конце, уже после наценок и свёртки вариантов. Для товаров дешевле шага округление автоматически делается мельче.",
                )}
              </Text>
            </Flex>

            {value && (
              <div className={css.controls}>
                <div className={css.field}>
                  <Text variant="body-1" className={css.fieldLabel}>
                    {t(
                      "export.rounding.step.label",
                      "До какого числа округлять",
                    )}
                  </Text>
                  <SegmentedRadioGroup
                    aria-label={t(
                      "export.rounding.step.aria",
                      "Шаг округления",
                    )}
                    size="l"
                    width="max"
                    value={String(value.zeros)}
                    onUpdate={(zeros) =>
                      field.onChange({ ...value, zeros: Number(zeros) })
                    }
                    options={STEP_OPTIONS}
                  />
                  <Text variant="caption-2" className={css.caption}>
                    {t(
                      "export.rounding.step.caption",
                      "Для недорогих товаров шаг уменьшается автоматически, чтобы не раздуть цену.",
                    )}
                  </Text>
                </div>

                <div className={css.field}>
                  <Text variant="body-1" className={css.fieldLabel}>
                    {t("export.rounding.direction.label", "Как округлять")}
                  </Text>
                  <SegmentedRadioGroup
                    aria-label={t(
                      "export.rounding.direction.aria",
                      "Направление округления",
                    )}
                    size="l"
                    width="max"
                    value={value.direction}
                    onUpdate={(direction) =>
                      field.onChange({
                        ...value,
                        direction: direction as RoundingDirection,
                      })
                    }
                    options={DIRECTION_OPTIONS}
                  />
                  <Text variant="caption-2" className={css.caption}>
                    {t(
                      "export.rounding.direction.caption",
                      "«Вверх» безопаснее: итоговая цена никогда не станет меньше.",
                    )}
                  </Text>
                </div>

                <div className={css.field}>
                  <Text variant="body-1" className={css.fieldLabel}>
                    {t("export.rounding.preview.label", "Как меняется цена")}
                  </Text>
                  <RoundingPreview sample={sample} option={value} />
                  <div className={css.sampleRow}>
                    {PREVIEW_SAMPLES.map((item) => (
                      <Button
                        key={item}
                        size="m"
                        selected={item === sample}
                        view={item === sample ? "normal" : "flat"}
                        onClick={() => setSample(item)}
                      >
                        {formatRub(item)}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      }}
    />
  );
};
